import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trophy, Flame, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANK_COLOR = (r) => r === 1 ? 'text-yellow-400' : r === 2 ? 'text-zinc-300' : r === 3 ? 'text-amber-600' : 'text-muted-foreground';

/**
 * Top-5 liderlik tablosu — Dashboard'da kullanıcıya kendi rank'ını gösterir.
 * Tenant-scoped (server tarafı). Opt-out olan kullanıcılar listede yok.
 */
export default function LeaderboardWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/leaderboard?limit=5`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        });
        if (r.ok) {
          const body = await r.json();
          if (!cancelled) setData(body);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !data) return null;
  if (data.leaderboard.length === 0) return null;

  return (
    <Card data-testid="leaderboard-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-500" />
          Liderlik Tablosu
          <Badge variant="outline" className="text-[10px]">şirket içi</Badge>
          {data.current_user_rank && (
            <span className="ml-auto text-xs text-muted-foreground">
              Senin rank'ın: <strong className="text-foreground">#{data.current_user_rank}</strong>
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {data.leaderboard.map(u => (
          <div
            key={u.id}
            className={`flex items-center gap-3 p-2 rounded-lg ${
              u.is_current_user ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/40'
            }`}
          >
            <span className={`w-6 text-center font-bold text-sm ${RANK_COLOR(u.rank)}`}>
              {u.rank === 1 ? '🥇' : u.rank === 2 ? '🥈' : u.rank === 3 ? '🥉' : `#${u.rank}`}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {u.is_current_user ? 'Sen' : u.name}
              </div>
              {u.department && (
                <div className="text-[10px] text-muted-foreground">{u.department}</div>
              )}
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-amber-500">{u.total_xp} XP</div>
              {u.current_streak > 0 && (
                <div className="text-[10px] text-orange-400 flex items-center gap-0.5 justify-end">
                  <Flame className="w-2.5 h-2.5" />{u.current_streak}g
                </div>
              )}
            </div>
          </div>
        ))}
        <Button size="sm" variant="ghost" onClick={() => navigate('/leaderboard')}
          className="w-full mt-2 h-7 text-xs">
          Tümünü gör + Takım rekabeti <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
}
